import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    IconButton,
    Chip,
    Divider,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowLeft,
    CheckCircle2,
    SquarePen,
    ChevronDown,
    Image as ImageIcon
} from 'lucide-react';
import parse from 'html-react-parser';
import { getHubsLocationById } from '../../Services/HubsLocations/HubsLocationsApi';

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const extractImageUrl = (item) => {
    if (!item) return null;
    let raw = item.images || item.image || item.coverImage || item.imageUrl || item.photo;
    if (!raw) return null;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
            try {
                raw = JSON.parse(trimmed);
            } catch {
                // keep string
            }
        }
    }
    if (Array.isArray(raw)) {
        if (raw.length === 0) return null;
        const first = raw[0];
        if (typeof first === 'string') return first.trim();
        if (first && typeof first === 'object') {
            return first.url || first.imageUrl || first.image || null;
        }
        return null;
    }
    if (typeof raw === 'object' && raw !== null) {
        return raw.url || raw.imageUrl || raw.image || null;
    }
    if (typeof raw === 'string' && raw.trim()) {
        return raw.trim();
    }
    return null;
};

const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return dateString || '--';
    }
};

const renderRichContent = (content) => {
    if (!content) return '--';
    if (typeof content !== 'string') return content;
    if (/<[a-z][\s\S]*>/i.test(content)) {
        try {
            return parse(content);
        } catch {
            return content;
        }
    }
    return content;
};

const HubLocationDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id: paramId } = useParams();
    const targetId = paramId || location.state?.id || location.state?.hubLocation?.id;

    const [hubLocation, setHubLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHubLocation = async () => {
            if (!targetId) {
                setError("Hub location ID is missing.");
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                setError(null);
                const res = await getHubsLocationById(targetId);
                if (res && res.success && res.data) {
                    setHubLocation(res.data);
                } else if (res && res.data) {
                    setHubLocation(res.data);
                } else {
                    setError("Failed to load hub location details.");
                }
            } catch (err) {
                console.error("Error fetching hub location:", err);
                setError("Failed to fetch hub location details.");
            } finally {
                setLoading(false);
            }
        };

        fetchHubLocation();
    }, [targetId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: '#FBFBFB' }}>
                <CircularProgress size={32} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error || !hubLocation) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        disableRipple
                        onClick={() => navigate('/hubs-locations')}
                        sx={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '8px',
                            p: 1
                        }}
                    >
                        <ArrowLeft size={18} color="#1E293B" />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1E293B' }}>
                        Hub Location Details
                    </Typography>
                </Box>
                <Alert severity="error" sx={{ borderRadius: '8px', border: '1px solid #FCA5A5' }}>
                    {error || "Hub location data not found. Please return to the hubs locations listing."}
                </Alert>
            </Box>
        );
    }

    const suitabilityList = safeParseArray(hubLocation.suitabilityItems);
    const faqsList = safeParseArray(hubLocation.faqs);
    const hubDisplayName = hubLocation.hubName || hubLocation.name || 'Hub Location Details';
    const hubDisplayLocation = hubLocation.location || hubLocation.locationName || (hubLocation.town && hubLocation.state ? `${hubLocation.town}, ${hubLocation.state}` : hubLocation.town || hubLocation.state) || '';
    const hubDisplayType = hubLocation.placementType || hubLocation.type || 'retreat';
    const isTraining = (hubDisplayType || '').toLowerCase() === 'training';
    const hubDisplayImage = extractImageUrl(hubLocation);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header / Action Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton
                        disableRipple
                        onClick={() => navigate('/hubs-locations')}
                        sx={{
                            bgcolor: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '8px',
                            p: 1,
                        }}
                    >
                        <ArrowLeft size={18} color="#1E293B" />
                    </IconButton>
                    <Box>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 600, color: '#1E293B' }}>
                                {hubDisplayName}
                            </Typography>
                            <Chip
                                label={isTraining ? 'Training' : 'Retreat'}
                                size="small"
                                sx={{
                                    bgcolor: isTraining ? '#F0F9FF' : '#F4F7F4',
                                    color: isTraining ? '#0369A1' : '#2E7D32',
                                    border: `1px solid ${isTraining ? '#BAE6FD' : '#D8E2D6'}`,
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    height: 24
                                }}
                            />
                            {hubLocation.status && (
                                <Chip
                                    label={hubLocation.status.charAt(0).toUpperCase() + hubLocation.status.slice(1)}
                                    size="small"
                                    sx={{
                                        bgcolor: hubLocation.status.toLowerCase() === 'active' ? '#F4F7F4' : '#F8FAFC',
                                        color: hubLocation.status.toLowerCase() === 'active' ? '#2E7D32' : '#64748B',
                                        border: `1px solid ${hubLocation.status.toLowerCase() === 'active' ? '#D8E2D6' : '#E2E8F0'}`,
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        height: 24
                                    }}
                                />
                            )}
                        </Stack>
                        <Typography sx={{ color: '#64748B', fontSize: '0.875rem', mt: 0.5, fontWeight: 400 }}>
                            {hubDisplayLocation ? `${hubDisplayLocation} • Hub Location Overview` : 'Hub Location Overview & Content'}
                        </Typography>
                    </Box>
                </Stack>

                <Button
                    disableElevation
                    disableRipple
                    variant="contained"
                    startIcon={<SquarePen size={16} />}
                    onClick={() => navigate(`/hubs-locations/edit/${hubLocation.id}`, { state: { hubLocation } })}
                    sx={{
                        bgcolor: '#FFBD65',
                        color: '#1E293B',
                        px: 2.5,
                        py: 1,
                        borderRadius: '8px',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#F5B052',
                            boxShadow: 'none'
                        }
                    }}
                >
                    Edit Location
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column: General Info, Headline, Description, FAQs */}
                <Grid size={{ xs: 12, md: 7 }}>
                    {/* General Information Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            mb: 3
                        }}
                    >
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B', mb: 2.5 }}>
                            General Information
                        </Typography>

                        <Grid container spacing={2.5}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Hub Name
                                </Typography>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                                    {hubLocation.hubName || hubLocation.name || '--'}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Destination Name
                                </Typography>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                                    {hubDisplayLocation || '--'}
                                </Typography>
                            </Grid>

                            {(hubLocation.state || hubLocation.town || hubLocation.location) && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        State & Town
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                                        {[hubLocation.location || hubLocation.town, hubLocation.state].filter(Boolean).join(', ') || '--'}
                                    </Typography>
                                </Grid>
                            )}

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    URL Slug
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        color: '#475569',
                                        fontFamily: 'monospace',
                                        bgcolor: '#F8FAFC',
                                        px: 1.25,
                                        py: 0.5,
                                        borderRadius: '6px',
                                        border: '1px solid #E2E8F0',
                                        display: 'inline-block'
                                    }}
                                >
                                    {hubLocation.pageUrl
                                        ? (hubLocation.pageUrl.startsWith('/') ? hubLocation.pageUrl : `/${hubLocation.pageUrl}`)
                                        : (hubLocation.slug ? (hubLocation.slug.startsWith('/') ? hubLocation.slug : `/${hubLocation.slug}`) : '--')}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Category
                                </Typography>
                                <Chip
                                    label={hubLocation.category?.name || '--'}
                                    size="small"
                                    sx={{ bgcolor: '#F8FAFC', color: '#334155', border: '1px solid #E2E8F0', fontWeight: 500, fontSize: '0.8rem', height: 26 }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Subcategory
                                </Typography>
                                <Chip
                                    label={hubLocation.subCategory?.name || '--'}
                                    size="small"
                                    sx={{ bgcolor: '#F8FAFC', color: '#334155', border: '1px solid #E2E8F0', fontWeight: 500, fontSize: '0.8rem', height: 26 }}
                                />
                            </Grid>
                        </Grid>

                        {hubLocation.heading && (
                            <>
                                <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />
                                <Box>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Headline
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 500, color: '#1E293B', lineHeight: 1.5 }}>
                                        {hubLocation.heading}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {hubLocation.introLine && (
                            <>
                                <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />
                                <Box>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Intro Line
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.925rem', fontWeight: 400, color: '#475569', lineHeight: 1.6 }}>
                                        {hubLocation.introLine}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />

                        <Box>
                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Description
                            </Typography>
                            <Box
                                sx={{
                                    fontSize: '0.925rem',
                                    color: '#334155',
                                    lineHeight: 1.7,
                                    fontWeight: 400,
                                    '& p': { mb: 1.5, '&:last-child': { mb: 0 } },
                                    '& h2, & h3': { fontWeight: 600, color: '#1E293B', mt: 2, mb: 1 },
                                    '& ul, & ol': { pl: 3, mb: 1.5 },
                                    '& li': { mb: 0.5 },
                                    '& a': { color: '#803927', textDecoration: 'underline' }
                                }}
                            >
                                {renderRichContent(hubLocation.description)}
                            </Box>
                        </Box>

                        {hubLocation.detailedContent && (
                            <>
                                <Divider sx={{ my: 2.5, borderColor: '#F1F5F9' }} />
                                <Box>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Detailed Content
                                    </Typography>
                                    <Box
                                        sx={{
                                            fontSize: '0.925rem',
                                            color: '#334155',
                                            lineHeight: 1.7,
                                            fontWeight: 400,
                                            '& p': { mb: 1.5, '&:last-child': { mb: 0 } },
                                            '& h2, & h3': { fontWeight: 600, color: '#1E293B', mt: 2, mb: 1 },
                                            '& ul, & ol': { pl: 3, mb: 1.5 },
                                            '& li': { mb: 0.5 },
                                            '& a': { color: '#803927', textDecoration: 'underline' }
                                        }}
                                    >
                                        {renderRichContent(hubLocation.detailedContent)}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Paper>

                    {/* Frequently Asked Questions Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                                Frequently Asked Questions
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 500 }}>
                                {faqsList.length} {faqsList.length === 1 ? 'question' : 'questions'}
                            </Typography>
                        </Box>

                        {faqsList.length > 0 ? (
                            <Stack spacing={1.25}>
                                {faqsList.map((faq, idx) => (
                                    <Accordion
                                        key={idx}
                                        elevation={0}
                                        disableGutters
                                        sx={{
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '8px !important',
                                            boxShadow: 'none',
                                            '&:before': { display: 'none' },
                                            '&.Mui-expanded': { bgcolor: '#FCFCFD' }
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ChevronDown size={17} color="#64748B" />}
                                            sx={{ px: 2, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}
                                        >
                                            <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#1E293B' }}>
                                                {typeof faq?.question === 'object' && faq?.question !== null ? faq.question.name || '' : (faq?.question || '--')}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0, borderTop: '1px solid #F1F5F9' }}>
                                            <Typography sx={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.65, fontWeight: 400, whiteSpace: 'pre-line', pt: 1.5 }}>
                                                {typeof faq?.answer === 'object' && faq?.answer !== null ? faq.answer.name || '' : (faq?.answer || '--')}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ py: 3.5, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px dashed #CBD5E1' }}>
                                <Typography sx={{ fontSize: '0.875rem', color: '#94A3B8', fontWeight: 400 }}>
                                    No FAQs added for this hub location.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Right Column: Cover Image, Suitability, System Details */}
                <Grid size={{ xs: 12, md: 5 }}>
                    {/* Location Cover Image */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            mb: 3
                        }}
                    >
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1E293B', mb: 1.5 }}>
                            Cover Image
                        </Typography>
                        {hubDisplayImage ? (
                            <Box>
                                <Box
                                    component="img"
                                    src={hubDisplayImage}
                                    alt={hubLocation.imageAltText || hubLocation.imageAlt || hubDisplayName || 'Hub location cover'}
                                    sx={{
                                        width: '100%',
                                        height: { xs: 200, sm: 260 },
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid #E2E8F0',
                                        display: 'block'
                                    }}
                                />
                                {(hubLocation.imageAltText || hubLocation.imageAlt) && (
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 1, fontWeight: 400 }}>
                                        Alt text: {hubLocation.imageAltText || hubLocation.imageAlt}
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    height: 180,
                                    borderRadius: '8px',
                                    bgcolor: '#F8FAFC',
                                    border: '1px dashed #CBD5E1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#94A3B8'
                                }}
                            >
                                <ImageIcon size={36} strokeWidth={1.5} />
                                <Typography sx={{ fontSize: '0.85rem', mt: 1, fontWeight: 400 }}>
                                    No cover image uploaded
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Suitability Guidelines Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none',
                            mb: 3
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1E293B' }}>
                                Suitability Guidelines
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 500 }}>
                                {suitabilityList.length} items
                            </Typography>
                        </Box>

                        {suitabilityList.length > 0 ? (
                            <Stack spacing={1}>
                                {suitabilityList.map((item, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            py: 1,
                                            px: 1.5,
                                            borderRadius: '6px',
                                            bgcolor: '#FBFBFB',
                                            border: '1px solid #F1F5F9',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.25
                                        }}
                                    >
                                        <CheckCircle2 size={16} color="#6E8964" style={{ marginTop: '2px', flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '0.85rem', color: '#334155', fontWeight: 400, lineHeight: 1.5 }}>
                                            {typeof item === 'object' && item !== null ? item.name || item.title || item.label || '' : item}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px dashed #CBD5E1' }}>
                                <Typography sx={{ fontSize: '0.85rem', color: '#94A3B8', fontWeight: 400 }}>
                                    No suitability items listed.
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Filter Criteria Card */}
                    {(hubLocation.dates || hubLocation.atmosphere || hubLocation.features || hubLocation.surroundingAreas || hubLocation.thingsInArea || hubLocation.mealPlan || hubLocation.language || (Array.isArray(hubLocation.experienceType) && hubLocation.experienceType.length > 0)) && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: '12px',
                                border: '1px solid #E2E8F0',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                mb: 3
                            }}
                        >
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                Filter Criteria
                            </Typography>
                            <Stack spacing={1.5}>
                                {hubLocation.dates && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Dates</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>{hubLocation.dates}</Typography>
                                    </Box>
                                )}
                                {hubLocation.atmosphere && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Atmosphere</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                            {hubLocation.atmosphere.title || hubLocation.atmosphere.name || String(hubLocation.atmosphere)}
                                        </Typography>
                                    </Box>
                                )}
                                {hubLocation.features && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Features</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                            {hubLocation.features.featureName || hubLocation.features.name || String(hubLocation.features)}
                                        </Typography>
                                    </Box>
                                )}
                                {hubLocation.surroundingAreas && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Surrounding Areas</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                            {hubLocation.surroundingAreas.surroundingName || hubLocation.surroundingAreas.name || String(hubLocation.surroundingAreas)}
                                        </Typography>
                                    </Box>
                                )}
                                {hubLocation.thingsInArea && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Things in Area</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                            {hubLocation.thingsInArea.name || String(hubLocation.thingsInArea)}
                                        </Typography>
                                    </Box>
                                )}
                                {hubLocation.mealPlan && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Meal Plan</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                            {hubLocation.mealPlan.mealName || hubLocation.mealPlan.name || String(hubLocation.mealPlan)}
                                        </Typography>
                                    </Box>
                                )}
                                {hubLocation.language && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem' }}>Language</Typography>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                            {hubLocation.language.name || String(hubLocation.language)}
                                        </Typography>
                                    </Box>
                                )}
                                {Array.isArray(hubLocation.experienceType) && hubLocation.experienceType.length > 0 && (
                                    <Box sx={{ pt: 0.5 }}>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.8rem', mb: 0.75 }}>Experience Types</Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {hubLocation.experienceType.map((exp, eIdx) => (
                                                <Chip
                                                    key={eIdx}
                                                    label={typeof exp === 'object' && exp !== null ? exp.name || exp.title || JSON.stringify(exp) : String(exp)}
                                                    size="small"
                                                    sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', color: '#334155', fontSize: '0.75rem', height: 24 }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    )}

                    {/* System Information Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: '12px',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none'
                        }}
                    >
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                            System Details
                        </Typography>

                        <Stack spacing={1.75}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.8rem', fontWeight: 400 }}>
                                    Location ID
                                </Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B', fontFamily: 'monospace' }}>
                                    {hubLocation.id || '--'}
                                </Typography>
                            </Box>

                            <Divider sx={{ borderColor: '#F1F5F9' }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.8rem', fontWeight: 400 }}>
                                    Created Date
                                </Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#334155' }}>
                                    {formatDate(hubLocation.createdAt)}
                                </Typography>
                            </Box>

                            <Divider sx={{ borderColor: '#F1F5F9' }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ color: '#64748B', fontSize: '0.8rem', fontWeight: 400 }}>
                                    Last Modified
                                </Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#334155' }}>
                                    {formatDate(hubLocation.updatedAt)}
                                </Typography>
                            </Box>

                            {(hubLocation.metaTitle || hubLocation.metaDescription || hubLocation.metaKeywords || hubLocation.targetKeyword || hubLocation.canonicalUrl || hubLocation.indexFollow) && (
                                <>
                                    <Divider sx={{ borderColor: '#F1F5F9' }} />
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1E293B', pt: 0.5 }}>
                                        SEO Metadata
                                    </Typography>
                                    {hubLocation.metaTitle && (
                                        <Box>
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400 }}>Meta Title</Typography>
                                            <Typography sx={{ fontSize: '0.825rem', color: '#334155', fontWeight: 500, mt: 0.25 }}>
                                                {hubLocation.metaTitle}
                                            </Typography>
                                        </Box>
                                    )}
                                    {(hubLocation.metaKeywords || hubLocation.targetKeyword) && (
                                        <Box>
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400 }}>Target Keywords</Typography>
                                            <Typography sx={{ fontSize: '0.825rem', color: '#334155', fontWeight: 500, mt: 0.25 }}>
                                                {hubLocation.metaKeywords || hubLocation.targetKeyword}
                                            </Typography>
                                        </Box>
                                    )}
                                    {hubLocation.metaDescription && (
                                        <Box>
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400 }}>Meta Description</Typography>
                                            <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 400, mt: 0.25, lineHeight: 1.5 }}>
                                                {hubLocation.metaDescription}
                                            </Typography>
                                        </Box>
                                    )}
                                    {hubLocation.canonicalUrl && (
                                        <Box>
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400 }}>Canonical URL</Typography>
                                            <Typography sx={{ fontSize: '0.775rem', color: '#803927', fontWeight: 400, mt: 0.25, wordBreak: 'break-all' }}>
                                                {hubLocation.canonicalUrl}
                                            </Typography>
                                        </Box>
                                    )}
                                    {hubLocation.indexFollow && (
                                        <Box>
                                            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 400 }}>Index Setting</Typography>
                                            <Typography sx={{ fontSize: '0.825rem', color: '#334155', fontWeight: 500, mt: 0.25 }}>
                                                {hubLocation.indexFollow}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default HubLocationDetails;
